/* eslint-disable max-lines */
import { 
  CategoryAttributeInterface, 
  AttributeNameEnum, 
  ItemAttributeAllowedValueInterface, 
  AuctionTypeEnum, 
  ListPaginationInterface, 
  ThumbnailAttachmentTypeEnum, 
  CategoryWithAttributesInterface,
  ReactionUsageContextTypeEnum,
  AuctionInfoWithItemIdInterface
} from '@on-arte/core-types';
import { 
  FilterDetails, 
  PaginationDetails, 
  UseLogger,
  UseState, 
  getPathWithParams, 
  useLogger, 
  usePagination
} from '@on-arte/ui';
import { RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { TransProps, useTranslation } from 'react-i18next';
import { QueryClient, QueryObserverResult, useQuery, useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';

import { getAttributes, getAuctions, getItemsCategories } from '@onArte/frontend/api/requests';
import { colorAttributeValues } from '@onArte/frontend/constants';
import { QueryKey } from '@onArte/frontend/enums';
import { useReactions } from '@onArte/frontend/hooks';
import { ArtworkMosaicItemDetailsWithItemId, FilterAttributeSettings, UseReactions } from '@onArte/frontend/interfaces';
import { FrontendApiError } from '@onArte/frontend/models';
import { UrlSearchParams } from '@onArte/frontend/types';
import { emptyRequest, getImageThumbnail } from '@onArte/frontend/utils';
import { ssrQueryKey } from '@onArte/shared/constants';
import { ItemSaleEditStatus, RouteNameEnum } from '@onArte/shared/enums';
import {
  ArtworkListFilterValues,
  artworkListCriteriaFromSearch,
  artworkListCriteriaKey,
  artworkListFilterValuesFromSearch,
  getRouteDetailsByName,
  mapArtworkListFilters
} from '@onArte/shared/utils';

import { UseArtworksList } from './artworksList.types';

export const useArtworksList: () => UseArtworksList = (): UseArtworksList => {
  const { 
    setMaxItems, 
    maxItems,
    itemsPerPage,
    setPage,
    offset,
    page
  }: PaginationDetails = usePagination(20);
  const { logger }: UseLogger = useLogger();
  const { t }: TransProps<never> = useTranslation();
  const { changeLikeState, isFavourite, favoritesReadyState, userFavouriteItemsIds }: UseReactions = useReactions();
  // Wszystkie wywołania `setSearchParams` niżej zapisują STAN widoku w adresie
  // (numer strony, wybrane filtry, identyfikator kafla do przewinięcia), a nie
  // przejście użytkownika. Dlatego `replace`: bez tego każda taka synchronizacja
  // dokładała wpis do historii i przycisk „wstecz" wracał do stanu pośredniego
  // tego samego listingu, sprawiając wrażenie, że nic się nie dzieje.
  const [searchParams, setSearchParams]: UrlSearchParams = useSearchParams();
  const [submittedFilters, setSubmittedFilters]: UseState<Record<string, number | string | string[]> | null>
    = useState<Record<string, number | string | string[]> | null>(null);
  const [attributes, setAttributes]: UseState<Record<string, CategoryAttributeInterface> | null> 
    = useState<Record<string, CategoryAttributeInterface> | null>(null);
  const [defaultFilters, setDefaultFilters]: UseState<Record<string, string[]> | undefined>
    = useState<Record<string, string[]> | undefined>(undefined);
  const [filters, setFilters]: UseState<FilterDetails[]> = useState<FilterDetails[]>([]);
  const [mosaicItems, setMosaicItems]: UseState<ArtworkMosaicItemDetailsWithItemId[] | null> 
    = useState<ArtworkMosaicItemDetailsWithItemId[] | null>(null);
  const [initialPageNumber, setInitialPageNumber]: UseState<number | null> = useState<number | null>(null);
  const [lowestLoadedPage, setLowestLoadedPage]: UseState<number | null> = useState<number | null>(null);
  const [highestLoadedPage, setHighestLoadedPage]: UseState<number | null> = useState<number | null>(null);
  const mosaicItemRef: RefObject<HTMLDivElement> = useRef(null);

  // Wydzielone z onSuccess, żeby dało się przekształcić dane także poza nim —
  // przy renderowaniu po stronie serwera onSuccess nigdy się nie wykonuje.
  const mapAuctionToMosaicItem: (item: AuctionInfoWithItemIdInterface) => ArtworkMosaicItemDetailsWithItemId = (
    item: AuctionInfoWithItemIdInterface
  ): ArtworkMosaicItemDetailsWithItemId => {
    const isItemLiked: boolean = isFavourite(item.itemId, ReactionUsageContextTypeEnum.Item);

    return {
      itemId: item.itemId,
      image: getImageThumbnail(item.coverPhoto, ThumbnailAttachmentTypeEnum.Size_340xAuto),
      internalPath: item.route?.prettyUrl
        ?? getPathWithParams(getRouteDetailsByName(RouteNameEnum.ArtworkDetails)?.url ?? '/', { id: item.id }),
      boxTitleDetails: {
        itemName: item.name,
        author: item.manufacturer.name,
        price: item.type === AuctionTypeEnum.PriceProposal ? undefined : item.price,
        biddersNumber: item.type === AuctionTypeEnum.Bidding ? item.offers : undefined,
        bottomButtonLabel: item.type === AuctionTypeEnum.PriceProposal ? t('onarte.common.priceProposal') : undefined,
        itemDescriptiveAttributes: [t(item.label)],
      },
      isLiked: isItemLiked,
      onLikeClick: (): void => changeLikeState(item.itemId, isItemLiked, ReactionUsageContextTypeEnum.Item),
      action: (): void => {
        searchParams.set('id', item.itemId);
        setSearchParams(searchParams, { replace: true });
      }
    };
  };

  const { refetch }: QueryObserverResult = useQuery(
    [QueryKey.AuctionsList, offset, submittedFilters, favoritesReadyState, initialPageNumber],
    (): Promise<ListPaginationInterface<AuctionInfoWithItemIdInterface> | void> => favoritesReadyState
      ? getAuctions({
        ...submittedFilters,
        offset,
        limit: itemsPerPage,
      })
      : emptyRequest(),
    {
      onSuccess: (data: ListPaginationInterface<AuctionInfoWithItemIdInterface> | void): void => {
        // Gdy favoritesReadyState jest jeszcze fałszywe, funkcja pobierająca zwraca
        // emptyRequest() — czyli `undefined`. onSuccess i tak zostaje wywołane, więc
        // bez tego warunku leci „Cannot read properties of undefined (reading 'amount')".
        // Ten sam błąd występuje dziś na produkcji przy każdym wejściu na listing.
        if (!data) {
          return;
        }

        setMaxItems(data.amount);
        const mosaicItemsList: ArtworkMosaicItemDetailsWithItemId[] = data.list.map(mapAuctionToMosaicItem);

        setMosaicItems([
          ...(initialPageNumber && page < initialPageNumber ? mosaicItemsList : []),
          ...(mosaicItems ?? []),
          ...(initialPageNumber && page >= initialPageNumber ? mosaicItemsList : [])
        ]);
        searchParams.set('page', String(page));
        setSearchParams(searchParams, { replace: true });
      },
      onError: (error: FrontendApiError): void => logger('getAuctionsAction', error)
    }
  );

  useEffect(
    (): void => {
      if (!mosaicItems) {
        return;
      }

      setMosaicItems(mosaicItems.map((item: ArtworkMosaicItemDetailsWithItemId): ArtworkMosaicItemDetailsWithItemId => {
        const isItemLiked: boolean = !!userFavouriteItemsIds.find((value: string) => value === item.itemId);
        return { 
          ...item, 
          isLiked: isItemLiked,
          onLikeClick: (): void => changeLikeState(item.itemId, isItemLiked, ReactionUsageContextTypeEnum.Item)
        };
      }));
    },
    [userFavouriteItemsIds]
  );

  useQuery(
    [QueryKey.ItemAttributes],
    (): Promise<Record<string, CategoryAttributeInterface>> => getAttributes(),
    {
      onSuccess: (data: Record<string, CategoryAttributeInterface>) => setAttributes(data),
      onError: (error: FrontendApiError) => logger(QueryKey.ItemAttributes, error)
    }
  );

  useQuery(
    [QueryKey.ItemCategories, attributes],
    (): Promise<CategoryWithAttributesInterface[]> => getItemsCategories(),
    {
      onSuccess: (data: CategoryWithAttributesInterface[]): void => {
        if (!Object.keys(attributes ?? {}).length) {
          return;
        }

        setFilters([
          {
            name: 'categoryIds',
            label: t('onarte.common.itemsAttributes.category.label'),
            options: data.map((category: CategoryWithAttributesInterface) => ({
              name: String(category.id),
              label: t(`onarte.common.itemsCategories.${category.name}`)
            }))
          },
          {
            name: 'subjectValues',
            label: t(`onarte.common.itemsAttributes.${AttributeNameEnum.Subject}.label`),
            options: getAttributesFiltersOptions(AttributeNameEnum.Subject)
          },
          {
            name: 'manufacturerIds',
            label: t('onarte.common.itemsAttributes.author.label'),
            autosuggestLabel: 'Wprowadź nazwę artysty',
          },
          {
            name: 'price',
            label: t('onarte.common.priceFilters.label'),
            options: [
              {
                name: '1-2500',
                label: t('onarte.common.priceFilters.lowestPrice.label')
              },
              {
                name: '2500-5000',
                label: t('onarte.common.priceFilters.lowPrice.label'),
              },
              {
                name: '5000-10000',
                label: t('onarte.common.priceFilters.mediumPrice.label'),
              },
              {
                name: 'over-10000',
                label: t('onarte.common.priceFilters.highPrice.label'),
              },
            ],
            isSingleChoice: true
          },
          {
            name: 'type',
            label: t('onarte.common.itemType.label'),
            options: [
              {
                name: ItemSaleEditStatus.Bidding,
                label: t('onarte.common.itemType.bidding.label'),
              },
              {
                name: ItemSaleEditStatus.BuyNow,
                label: t('onarte.common.itemType.buyNow.label'),
              },
              {
                name: ItemSaleEditStatus.PriceProposal,
                label: t('onarte.common.itemType.priceProposal.label'),
              }
            ],
            isSingleChoice: true
          },
          {
            name: 'materialValues',
            label: t(`onarte.common.itemsAttributes.${AttributeNameEnum.Material}.label`),
            options: getAttributesFiltersOptions(AttributeNameEnum.Material)
          },
          {
            name: 'colorValues',
            label: t(`onarte.common.itemsAttributes.${AttributeNameEnum.Color}.label`),
            options: getAttributesFiltersOptions(AttributeNameEnum.Color)
          },
          {
            name: 'size',
            label: t('onarte.common.size.label'),
            options: [
              {
                name: 'small',
                label: t('onarte.common.size.small.label'),
              },
              {
                name: 'medium',
                label: t('onarte.common.size.medium.label'),
              },
              {
                name: 'large',
                label: t('onarte.common.size.large.label'),
              },
            ],
            isSingleChoice: true
          },
          {
            name: 'techniqueValues',
            label: t(`onarte.common.itemsAttributes.${AttributeNameEnum.Technique}.label`),
            options: getAttributesFiltersOptions(AttributeNameEnum.Technique)
          },
          {
            name: 'styleValues',
            label: t(`onarte.common.itemsAttributes.${AttributeNameEnum.Style}.label`),
            options: getAttributesFiltersOptions(AttributeNameEnum.Style)
          },
        ]);
      },
      onError: (error: FrontendApiError) => logger(QueryKey.ItemCategories, error)
    } 
  );

  useEffect(
    (): void => {
      // Ten sam odczyt, którego używa serwer — patrz `artworkListFilterValuesFromSearch`.
      const filterValues: Record<string, string[]> = artworkListFilterValuesFromSearch(searchParams.toString());

      if (searchParams.get('page')) {
        setPage(Number(searchParams.get('page')));
        if (!initialPageNumber) {
          setInitialPageNumber(Number(searchParams.get('page')));
        }
        if (!lowestLoadedPage) {
          setLowestLoadedPage(Number(searchParams.get('page')));
        }
        if (!highestLoadedPage) {
          setHighestLoadedPage(Number(searchParams.get('page')));
        }
      }
      
      if (!defaultFilters) {
        setDefaultFilters(filterValues);
        onFilterHandler(filterValues);
      }
    },
    [searchParams.toString()]
  );

  useEffect(
    (): void => {
      if (mosaicItems?.length) {
        searchParams.delete('page');
        setSearchParams(searchParams, { replace: true });
        changePageAction(1);
        setMosaicItems(null);
      }

      void refetch();
    }, 
    [submittedFilters]
  );
    
  const onFilterHandler: (filtersValues: Record<string, string[]>) => void = (filtersValues: Record<string, string[]>): void => {
    // Odwzorowanie filtrów na parametry pobrania jest WSPÓLNE z renderowaniem po stronie
    // serwera (shared/utils). Gdy istniało wyłącznie tutaj, serwer zasiewał listing zawsze
    // niefiltrowany i adres z filtrem oddawał inny zestaw dzieł, niż widok pokazuje
    // po podłączeniu.
    const filtersValuesObject: ArtworkListFilterValues = mapArtworkListFilters(filtersValues);

    setSubmittedFilters(filtersValuesObject);
    
    if (!Object.entries(filtersValuesObject).length && !searchParams.get('id')) {
      setSearchParams({}, { replace: true });
    } else {
      Object.entries(filtersValuesObject)
        .forEach((filter: [string, string | number | string[]]): void => {
          searchParams.set(filter[0], String(filter[1]));
        });
      setSearchParams(searchParams, { replace: true });
    }
  };

  const getAttributesFiltersOptions: (attributeName: AttributeNameEnum) => FilterAttributeSettings[] = (
    attributeName: AttributeNameEnum
  ): FilterAttributeSettings[] => {
    return attributes
      ? attributes[attributeName].values.map((attributeValueObject: ItemAttributeAllowedValueInterface): FilterAttributeSettings => ({
        name: attributeValueObject.value,
        label: t(`onarte.common.itemsAttributes.${attributeName}.options.${attributeValueObject.value}.label`),
        ...(attributeName === AttributeNameEnum.Color ? { color: colorAttributeValues[attributeValueObject.value] } : {})
      }))
      : [];
  };

  const scrollToPreviousAction: () => void = (): void => {
    if (!!submittedFilters && !!searchParams.get('id') && mosaicItems) {
      setMosaicItems(mosaicItems.map((item: ArtworkMosaicItemDetailsWithItemId): ArtworkMosaicItemDetailsWithItemId => {
        return { 
          ...item, 
          ref: searchParams.get('id') === item.itemId ? mosaicItemRef : undefined
        };
      }));
      mosaicItemRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      searchParams.delete('id');
      setSearchParams(searchParams, { replace: true });
    }
  };

  const changePageAction: (newPage: number) => void = (newPage: number): void => {
    setPage(newPage);
    if (initialPageNumber && newPage >= initialPageNumber) {
      setHighestLoadedPage(newPage);
    } else {
      setLowestLoadedPage(newPage);
    }
  };

  // ── Renderowanie po stronie serwera ────────────────────────────────────────
  // Serwer wypełnia pamięć podręczną pod stabilnym kluczem (ssrPrefetch.helper.ts),
  // bo klucza używanego przez powyższe useQuery nie da się na serwerze odtworzyć —
  // zawiera `favoritesReadyState`, które przełącza się dopiero w useEffect.
  //
  // Te dane służą wyłącznie pierwszemu renderowaniu. Gdy tylko zapytanie kliencki
  // zwróci wynik i ustawi `mosaicItems`, przestają być używane, a doładowywanie
  // kolejnych stron działa jak dotąd.
  // Klucz zawiera odcisk kryteriów z ADRESU. Po zmianie filtrów widok czyści swoje
  // wyniki, a bez odcisku wartość zapasowa oddawała komplet spod pierwotnego adresu:
  // użytkownik widział prace spoza wybranego filtra, a przy nieudanym pobraniu zostawał
  // przy nich na stałe. Z odciskiem klucz po prostu nie trafia i zasiew milknie.
  const queryClient: QueryClient = useQueryClient();
  const ssrAuctionsKey: [string, string] = useMemo(
    (): [string, string] => ssrQueryKey(
      `${QueryKey.AuctionsList}-${artworkListCriteriaKey(artworkListCriteriaFromSearch(searchParams.toString()))}`
    ),
    [searchParams.toString()]
  );
  const ssrAuctions: ListPaginationInterface<AuctionInfoWithItemIdInterface> | undefined = queryClient
    .getQueryData<ListPaginationInterface<AuctionInfoWithItemIdInterface>>(ssrAuctionsKey);

  // `userFavouriteItemsIds` MUSI być w zależnościach: `mapAuctionToMosaicItem` czyta
  // polubienia w chwili odwzorowania, a dla zalogowanego przychodzą one po pierwszym
  // renderowaniu. Efekt odświeżający serduszka wychodzi wcześnie, dopóki `mosaicItems`
  // jest puste, więc bez tego polubione prace miały puste serce, a kliknięcie próbowało
  // polubić zamiast odlubić.
  const ssrMosaicItems: ArtworkMosaicItemDetailsWithItemId[] | null = useMemo(
    (): ArtworkMosaicItemDetailsWithItemId[] | null => ssrAuctions
      ? ssrAuctions.list.map(mapAuctionToMosaicItem)
      : null,
    [ssrAuctions, userFavouriteItemsIds]
  );

  return {
    onFilterHandler,
    scrollToPreviousAction,
    mosaicItems: mosaicItems ?? ssrMosaicItems,
    filters,
    defaultFilters,
    maxItems: maxItems || (ssrAuctions?.amount ?? 0),
    changePageAction,
    page,
    itemsPerPage,
    initialPageNumber,
    lowestLoadedPage,
    highestLoadedPage,
  };
};
